import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import type { Tables } from "@how/database";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

export type HouseholdRow = Pick<
  Tables<"households">,
  "id" | "name" | "locality" | "region" | "created_at"
> & { readonly memberCount: number };

export type HouseholdSort = { readonly id: "name" | "created_at"; readonly desc: boolean };

export interface HouseholdsQuery {
  readonly search: string;
  readonly pageIndex: number;
  readonly pageSize: number;
  readonly sort: HouseholdSort;
}

/** Households, filtered, sorted and paged by the server, with a member count per row from one embedded aggregate. */
export function useHouseholds(q: HouseholdsQuery) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["households", tenantId, q],
    enabled: tenantId !== null,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<{ rows: HouseholdRow[]; total: number }> => {
      const from = q.pageIndex * q.pageSize;
      let query = supabase
        .from("households")
        .select("id, name, locality, region, created_at, household_memberships(count)", {
          count: "exact",
        })
        // The policies admit every church the reader belongs to; this keeps the list to the one on screen.
        .eq("tenant_id", tenantId ?? "")
        .is("deleted_at", null)
        .order(q.sort.id, { ascending: !q.sort.desc })
        .order("id", { ascending: true })
        .range(from, from + q.pageSize - 1);
      const trimmed = q.search.trim();
      if (trimmed.length > 0) {
        query = query.ilike("name", `%${trimmed.replace(/[%_]/g, "")}%`);
      }
      const { data, error, count } = await query;
      if (error) throw error;
      return {
        rows: data.map(({ household_memberships: members, ...household }) => ({
          ...household,
          memberCount: members[0]?.count ?? 0,
        })),
        total: count ?? 0,
      };
    },
  });
}

export interface HouseholdMember {
  readonly membershipId: string;
  readonly personId: string;
  readonly name: string;
  readonly role: Tables<"household_memberships">["role"];
  readonly isPrimaryContact: boolean;
}

export interface HouseholdDetail {
  readonly household: Tables<"households">;
  readonly members: readonly HouseholdMember[];
}

export function useHousehold(householdId: string | null) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["household", tenantId, householdId],
    enabled: householdId !== null,
    queryFn: async (): Promise<HouseholdDetail> => {
      const id = householdId ?? "";
      const [household, members] = await Promise.all([
        supabase.from("households").select("*").eq("id", id).single(),
        supabase
          .from("household_memberships")
          .select("id, role, is_primary_contact, people(id, first_name, last_name)")
          .eq("household_id", id),
      ]);
      if (household.error) throw household.error;
      if (members.error) throw members.error;
      const order = { adult: 0, other: 1, child: 2 } as const;
      return {
        household: household.data,
        members: members.data
          .map((row) => ({
            membershipId: row.id,
            personId: row.people.id,
            name: `${row.people.first_name} ${row.people.last_name}`,
            role: row.role,
            isPrimaryContact: row.is_primary_contact,
          }))
          // Adults first, then children, each alphabetically: how a family reads on paper.
          .sort((a, b) => order[a.role] - order[b.role] || a.name.localeCompare(b.name)),
      };
    },
  });
}

/** People not yet in this household, for the add-member picker. Server-side search, capped for a dropdown. */
export function usePeopleOptions(search: string, excluded: readonly string[]) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["people-options", tenantId, search, excluded],
    enabled: tenantId !== null,
    queryFn: async (): Promise<{ value: string; label: string }[]> => {
      let query = supabase
        .from("people")
        .select("id, first_name, last_name")
        .eq("tenant_id", tenantId ?? "")
        .is("deleted_at", null)
        .order("last_name")
        .order("first_name")
        .limit(20);
      const trimmed = search.trim().replace(/[%_]/g, "");
      if (trimmed.length > 0) {
        query = query.or(`first_name.ilike.%${trimmed}%,last_name.ilike.%${trimmed}%`);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data
        .filter((p) => !excluded.includes(p.id))
        .map((p) => ({ value: p.id, label: `${p.last_name}, ${p.first_name}` }));
    },
  });
}

function useHouseholdInvalidation() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return async (householdId?: string) => {
    await client.invalidateQueries({ queryKey: ["households", tenantId] });
    // A membership change shows up on a person's record and timeline too.
    await client.invalidateQueries({ queryKey: ["person", tenantId] });
    if (householdId !== undefined) {
      await client.invalidateQueries({ queryKey: ["household", tenantId, householdId] });
    }
  };
}

export interface HouseholdFields {
  readonly name: string;
  readonly addressLine1: string;
  readonly addressLine2: string;
  readonly locality: string;
  readonly region: string;
  readonly postalCode: string;
}

/** Blank strings become nulls, so clearing a field in the form clears it in the database. */
function toArgs(fields: HouseholdFields) {
  const orNull = (value: string): string | undefined =>
    value.trim().length === 0 ? undefined : value.trim();
  const optional = (key: string, value: string) => {
    const v = orNull(value);
    return v === undefined ? {} : { [key]: v };
  };
  return {
    ...optional("p_address_line1", fields.addressLine1),
    ...optional("p_address_line2", fields.addressLine2),
    ...optional("p_locality", fields.locality),
    ...optional("p_region", fields.region),
    ...optional("p_postal_code", fields.postalCode),
    ...(orNull(fields.addressLine1) === undefined && orNull(fields.locality) === undefined
      ? {}
      : { p_country_code: "US" }),
  };
}

export function useCreateHousehold() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useHouseholdInvalidation();
  return useMutation({
    mutationFn: async (fields: HouseholdFields): Promise<string> => {
      const { data, error } = await supabase.rpc("create_household", {
        p_tenant_id: tenantId ?? "",
        p_name: fields.name.trim(),
        ...toArgs(fields),
      });
      if (error) throw error;
      return data;
    },
    onSuccess: async (householdId) => {
      await invalidate(householdId);
    },
  });
}

export function useUpdateHousehold() {
  const supabase = useSupabase();
  const invalidate = useHouseholdInvalidation();
  return useMutation({
    mutationFn: async (input: {
      readonly householdId: string;
      readonly fields: HouseholdFields;
    }) => {
      const { error } = await supabase.rpc("update_household", {
        p_household_id: input.householdId,
        p_name: input.fields.name.trim(),
        ...toArgs(input.fields),
      });
      if (error) throw error;
    },
    onSuccess: async (_result, input) => {
      await invalidate(input.householdId);
    },
  });
}

export function useDeleteHousehold() {
  const supabase = useSupabase();
  const invalidate = useHouseholdInvalidation();
  return useMutation({
    mutationFn: async (householdId: string) => {
      const { error } = await supabase.rpc("delete_household", { p_household_id: householdId });
      if (error) throw error;
    },
    onSuccess: async () => {
      await invalidate();
    },
  });
}

export function useSetHouseholdMember() {
  const supabase = useSupabase();
  const invalidate = useHouseholdInvalidation();
  return useMutation({
    mutationFn: async (input: {
      readonly householdId: string;
      readonly personId: string;
      readonly role: HouseholdMember["role"];
      readonly isPrimaryContact: boolean;
    }) => {
      const { error } = await supabase.rpc("set_household_member", {
        p_household_id: input.householdId,
        p_person_id: input.personId,
        p_role: input.role,
        p_is_primary_contact: input.isPrimaryContact,
      });
      if (error) throw error;
    },
    onSuccess: async (_result, input) => {
      await invalidate(input.householdId);
    },
  });
}

export function useRemoveHouseholdMember() {
  const supabase = useSupabase();
  const invalidate = useHouseholdInvalidation();
  return useMutation({
    mutationFn: async (input: { readonly householdId: string; readonly membershipId: string }) => {
      const { error } = await supabase.rpc("remove_household_member", {
        p_membership_id: input.membershipId,
      });
      if (error) throw error;
    },
    onSuccess: async (_result, input) => {
      await invalidate(input.householdId);
    },
  });
}
